













































































































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import axios, { AxiosInstance } from 'axios';
import moment from 'moment';
import _ from 'lodash';
import Swal from 'sweetalert2';
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

interface Appointment {
  id: string;
  scheduleKey: string;
  firstName: string;
  type: string;
  status: string;
  statusName: string;
  statusBackColor: string;
  statusFontColor: string;
  lang: string;
  calendarName: string;
  reasonName: string;
  dateTime: string;
  expirationDate: string;
  showDateTime: string;
  calendarBackColor?: string;
  calendarTextColor?: string;
  confirmBackColor?: string;
  confirmTextColor?: string;
  cancelBackColor?: string;
  cancelTextColor?: string;
  longDescription?: string;
  longDescriptionES?: string;
  entityName?: string;
  locationAddrLine1?: string;
  locationAddrLine2?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  locationPhone?: string;
  location?: string;
  serviceDescription?: string;
  serviceAddress?: { line1: string; line2: string; city: string; state: string; zip: string };
  descriptionReason?: string;
  showDescriptionReason?: string;
  priorityPrice?: string;
  patientConfirm?: string;
  patientCancel?: string;
  noCancelTime?: string;
  ArrivalCheckinTime?: string;
  archived?: string;
  date?: string;
  time?: string;
}

interface Prerequisite {
  id: string;
  name: string;
  typeId: 'BIN' | 'PIC' | 'INF' | 'FRM';
  isOptional: string;
  value?: string;
  fileName?: string;
  fontStyle?: string;
  formPercent?: number;
  fileKey?: string;
}

interface StatusButton {
  Status: number;
  Name: string;
  StatusText: string;
  Icon?: string;
  BackColor?: string;
  FontColor?: string;
  disabled?: boolean;
}

interface MessageLog {
  id: string;
  type: string;
  text: string;
  date: string;
  read: boolean;
}

@Component({
  components: {
    VueLoading
  }
})
export default class AppointmentConfirmation extends Vue {
  private api: AxiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_URL || '/api',
    timeout: 60000
  });

  appt_key = this.$route.params.appt_key;

  logoWmPath: string = require('@/assets/wmpc_logo.png');
  bannerDefaultImg: string = require('@/assets/appt_banner.gif');

  appt: Appointment | null = null;
  apptUrls: Array<{ url: string; description: string }> = [];
  prerequisites: Prerequisite[] = [];
  tempPrerequisites: Prerequisite[] = [];
  prerequisiteFiles: Array<{ file: File; position: number }> = [];
  btnList: StatusButton[] = [];
  translations: Record<string, Record<string, string>> | null = null;
  langCode: string = '';
  currentLang: boolean = true;
  urlFiles: string = '';
  messages: MessageLog[] = [];
  comment: string = '';
  entityLogo: string | null = null;
  entityLogoPosition: string | null = null;
  entityBanner: string | null = null;
  showArrival: boolean = false;
  apptIsOld: boolean = false;
  loading: boolean = false;

  async mounted(): Promise<void> {
    await this.getTranslations();
    await this.getAppt();
    this.getMessages();
    this.getLogo();
    this.getBanner();
    this.getApptUrls();
    this.apptViewed();
  }

  @Watch('currentLang')
  onCurrentLangChange(): void {
    if (this.langCode === 'EN') {
      this.langCode = 'ES';
    } else if (this.langCode === 'ES') {
      this.langCode = 'EN';
    }
  }

  get targetLangCode(): string {
    return this.langCode === 'EN' ? 'ES' : 'EN';
  }

  get isRegularAppt(): boolean {
    return this.appt ? this.appt.type === 'APT' : false;
  }

  get isServiceAppt(): boolean {
    return this.appt ? this.appt.type !== 'APT' : false;
  }

  get unreadMessage(): MessageLog | undefined {
    return this.messages.find(m => !m.read && m.type === 'OPM');
  }

  get isChangePreqs(): boolean {
    return !_.isEqual(this.prerequisites, this.tempPrerequisites);
  }

  get isExpiredAppt(): boolean {
    if (this.appt) {
      return moment().isAfter(moment(this.appt.expirationDate)) || this.appt.archived === '1';
    }
    return false;
  }

  get noCancelTime(): boolean {
    if (!this.appt || this.appt.noCancelTime === '0') {
      return false;
    }
    const currentTime = moment();
    const eventTime = moment(this.appt.dateTime);
    const duration = moment.duration(eventTime.diff(currentTime));
    return duration.asMinutes() <= parseInt(this.appt.noCancelTime || '0', 10);
  }

  get apptDate(): string {
    if (!this.appt || !this.appt.dateTime) return '';

    const dateTime = moment(this.appt.dateTime, 'YYYY-MM-DD HH:mm:ss.SSS');
    if (!dateTime.isValid()) return '';

    const date = dateTime
      .locale(this.langCode === 'ES' ? 'es' : 'en')
      .format('dddd MMM D');

    const formattedDate = date.charAt(0).toUpperCase() + date.slice(1);
    const time = dateTime.format('hh:mm A');

    if (this.appt.showDateTime === 'DT') {
      return `${formattedDate}, ${time}`;
    }

    if (this.appt.showDateTime === 'TI') {
      return time;
    }

    return formattedDate;
  }

  getImagePath(id: string | null): string {
    return id ? `${this.api.defaults.baseURL}/image?id=${id}` : '';
  }

  async getAppt(): Promise<void> {
    this.loading = true;
    try {
      const params = new FormData();
      params.append('key', this.appt_key);

      const res = await Vue.axios.post('schedule/appt/details', params);
      this.appt = res.data;

      this.checkApptIsOld();
      this.arrivalTimeLeft();

      if (this.appt) {
        this.langCode = this.appt.lang;
        this.getPrerequisites();
        this.getUrlFiles();
        this.getBtnList();
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async updateApptLang(): Promise<void> {
    if (!this.appt) return;
    this.loading = true;
    try {
      await this.api.post('appt/lang', { key: this.appt.scheduleKey });
      this.getBtnList();
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async getMessages(): Promise<void> {
    try {
      const params = new FormData();
      params.append('key', this.appt_key);

      const res = await Vue.axios.post('schedule/appt/messages', params);

      this.messages = res.data
        .map((log: any) => ({
          id: log.id,
          type: log.typeId,
          text: log.comment,
          date: moment(log.created).isSame(moment(), 'day')
            ? moment(log.created).format('hh:mm A')
            : moment(log.created).format('MM/DD/YYYY hh:mm A'),
          read: Boolean(log.isRead)
        }))
        .sort((a: any, b: any) => Number(a.id) - Number(b.id));

      if (this.unreadMessage) {
        this.showUnreadMessages();
      }
    } catch (err) {
      console.error(err);
    }
  }

  async getApptUrls(): Promise<void> {
    try {
      const res = await this.api.post('urls');
      this.apptUrls = res.data;
    } catch (err) {
      console.error(err);
    }
  }

  async getPrerequisites(): Promise<void> {
    try {
      const params = new FormData();
      params.append('key', this.appt_key);

      const res = await Vue.axios.post('schedule/appt/prerequisites', params);
      this.prerequisites = res.data;
      this.tempPrerequisites = _.cloneDeep(res.data);
    } catch (err) {
      console.error(err);
    }
  }

  async getBtnList(): Promise<void> {
    try {
      const params = new FormData();
      params.append('key', this.appt_key);

      const res = await Vue.axios.post('schedule/appt/btn/list', params);
      this.btnList = res.data;

      if (!this.appt) return;

      const hasMatchingStatus = this.btnList.some(btn => btn.Status.toString() === this.appt?.status);
      const hasPriorityPrice = Boolean(parseFloat(this.appt.priorityPrice || '0'));

      if (hasMatchingStatus || hasPriorityPrice) {
        this.disableBtn();
      }

      if (
        this.appt.status === '2' &&
        !this.isExpiredAppt &&
        this.btnList.some(btn => btn.Status === 4) &&
        !hasPriorityPrice
      ) {
        this.showConfirmAlert();
      }
    } catch (err) {
      console.error(err);
    }
  }

  async getLogo(): Promise<void> {
    try {
      const res = await this.api.post('logo');
      this.entityLogo = res.data.id;
      this.entityLogoPosition = res.data.position;
    } catch (err) {
      console.error(err);
    }
  }

  async getBanner(): Promise<void> {
    try {
      const res = await this.api.post('banner');
      this.entityBanner = res.data;
    } catch (err) {
      console.error(err);
    }
  }

  showUnreadMessages(): void {
    if (!this.unreadMessage) return;

    Swal.fire({
      title: 'New Message',
      text: this.unreadMessage.text,
      showCancelButton: false,
      confirmButtonText: 'OK'
    }).then(async () => {
      if (!this.unreadMessage) return;
      const { id } = this.unreadMessage;
      try {
        await this.api.post('messages/read', { id });
        if (this.unreadMessage) this.unreadMessage.read = true;
        if (this.unreadMessage) this.showUnreadMessages();
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    });
  }

  showConfirmAlert(): void {
    Swal.fire({
      title: this.translate('Confirmation'),
      text: this.translate('Do you want to confirm the appointment now?'),
      showCancelButton: true,
      confirmButtonText: this.translate('Yes'),
      cancelButtonText: this.translate('Later')
    }).then((result) => {
      if (result.isConfirmed) {
        this.confirm();
      }
    });
  }

  arrivalTimeLeft(): void {
    if (!this.appt) return;

    const currentTime = moment();
    const eventTime = moment(this.appt.dateTime);
    let duration = moment.duration(eventTime.diff(currentTime));
    const checkinTime = parseInt(this.appt.ArrivalCheckinTime || '0', 10);

    if (
      currentTime.format('YYYY-MM-DD') !== moment(this.appt.dateTime).format('YYYY-MM-DD') ||
      this.appt.ArrivalCheckinTime === '0'
    ) {
      return;
    }

    if (
      duration.asMinutes() <= checkinTime &&
      this.appt.status !== '6' &&
      !parseFloat(this.appt.priorityPrice || '0')
    ) {
      this.showArrival = true;
      return;
    }

    const intervalId = setInterval(() => {
      if (duration.asMinutes() <= checkinTime) {
        this.showArrival = true;
        clearInterval(intervalId);
      }
      duration = moment.duration(duration.asMinutes() - 1, 'minutes');
    }, 60000);
  }

  async arrival(): Promise<void> {
    if (!this.appt) return;
    this.loading = true;
    try {
      await this.api.post('arrival', { id: this.appt.id, status: '5' });
      this.appt.status = '5';
      Swal.fire(this.translate('Thank you, we have reported your arrival.'), '', 'success');
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async changeStatus(btn: StatusButton): Promise<void> {
    if (!this.appt) return;
    this.loading = true;
    try {
      await this.api.post('status', { status: btn.Status });
      this.appt.status = btn.Status.toString();
      Swal.fire(`${this.translate('Thank you')}, ${btn.StatusText.toLowerCase()}.`, '', 'success');
      this.disableBtn();
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  disableBtn(): void {
    this.btnList.forEach(btn => {
      if (btn.Status !== 6) {
        btn.disabled = true;
      }
    });
  }

  checkApptIsOld(): void {
    if (this.appt) {
      this.apptIsOld = moment(moment().format('YYYY-MM-DD')).isAfter(
        moment(this.appt.dateTime).format('YYYY-MM-DD')
      );
    }
  }

  async saveComment(): Promise<void> {
    if (!this.comment.trim()) return;
    this.loading = true;
    try {
      await this.api.post('comment', { comment: this.comment });
      this.comment = '';
      await this.getMessages();
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  goToForm(preReqId: string): void {
    this.$router.push({ name: 'form', params: { id: preReqId } });
  }

  pay(): void {
    this.$router.push({ name: 'pay' });
  }

  async confirm(): Promise<void> {
    if (!this.appt) return;
    this.loading = true;
    try {
      await this.api.post('confirm');
      this.appt.status = '4';
      Swal.fire({
        title: this.translate('Confirmation'),
        text: this.translate('Thank you, appointment confirmed'),
        showCancelButton: false,
        confirmButtonText: this.translate('Continue')
      });
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  ical(): void {
    window.open(`${this.api.defaults.baseURL}/ical`, '_blank');
  }

  async cancel(): Promise<void> {
    const res = await Swal.fire({
      title: this.translate('Are you sure?'),
      showCancelButton: true,
      confirmButtonText: this.translate('Yes'),
      cancelButtonText: 'No'
    });

    if (res.isConfirmed) {
      this.loading = true;
      try {
        await this.api.post('cancel');
        if (this.appt) this.appt.status = '6';
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    }
  }

  async apptViewed(): Promise<void> {
    this.loading = true;
    try {
      await this.api.post('appt/viewed');
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async getUrlFiles(): Promise<void> {
    try {
      const res = await this.api.post('files/url');
      this.urlFiles = res.data;
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async savePrerequisites(): Promise<void> {
    if (!this.prerequisites || !this.isChangePreqs) return;

    if (this.prerequisiteFiles.length > 0) {
      await this.uploadPrerequisiteFiles();
      return;
    }

    this.loading = true;
    try {
      await this.api.post('prerequisite/save', { prerequisites: this.prerequisites });
      await this.getPrerequisites();

      if (this.appt) {
        this.appt.statusName = 'Requested';
        this.appt.statusBackColor = '#FFFFFF';
        this.appt.statusFontColor = '#004598';
      }

      Swal.fire(this.translate('Thank you, document(s) uploaded.'), '', 'success');
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async uploadPrerequisiteFiles(): Promise<void> {
    this.loading = true;
    const formData = new FormData();

    this.prerequisiteFiles.forEach((item, index) => {
      formData.append(`files[${index}]`, item.file);
    });

    try {
      const res = await this.api.post(this.urlFiles + 'ul', formData);
      this.prerequisiteFiles.forEach((item, index) => {
        this.prerequisites[item.position].fileKey = res.data[index];
      });

      this.prerequisiteFiles = [];
      this.loading = false;
      await this.savePrerequisites();
    } catch (err) {
      console.error(err);
      this.loading = false;
    }
  }

  addPrerequisiteFiles(event: Event, index: number, preReqType: string): void {
    const target = event.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) return;

    const file = target.files[0];

    if (preReqType === 'PIC' && !this.fileIsImage(file.name)) {
      Swal.fire({
        title: 'Warning',
        text: this.translate('Invalid image.'),
        icon: 'warning'
      });
      return;
    }

    const tempIndex = this.prerequisiteFiles.findIndex(f => f.position === index);
    if (tempIndex !== -1) {
      this.prerequisiteFiles[tempIndex].file = file;
    } else {
      this.prerequisiteFiles.push({ file, position: index });
    }

    this.prerequisites[index].fileName = file.name;
  }

  fileIsImage(fileName: string): boolean {
    const ext = this.fileExt(fileName);
    const validExtensions = ['jpg', 'jpeg', 'tiff', 'gif', 'bmp', 'png', 'webp', 'svg', 'ai', 'eps'];
    return validExtensions.includes(ext);
  }

  fileExt(fileName: string): string {
    return fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  }

  translate(key: string): string {
    if (!key || key.includes('undefined') || key === undefined) return key;
    if (!this.translations) return key;

    const currentLangCode = this.langCode === 'EN' ? 'EN' : 'ES';
    if (this.appt) this.appt.lang = currentLangCode;

    return this.translations[currentLangCode]?.[key] ?? key;
  }

  async getTranslations(): Promise<void> {
    try {
      const response = await fetch('/assets/translations.json');
      this.translations = await response.json();
    } catch (err) {
      console.error('Error loading translations:', err);
    }
  }

  formatText(text: string): string {
    if (!text) return '';
    return text
      .replace(/(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)/g, '<b>$1</b>')
      .replace(/(?:_)(?:(?!\s))((?:(?!\n|_).)+)(?:_)/g, '<i>$1</i>')
      .replace(/(?:~)(?:(?!\s))((?:(?!\n|~).)+)(?:~)/g, '<s>$1</s>')
      .replace(/(?:--)(?:(?!\s))((?:(?!\n|--).)+)(?:--)/g, '<u>$1</u>')
      .replace(/(?:```)(?:(?!\s))((?:(?!\n|```).)+)(?:```)/g, '<tt>$1</tt>')
      .replace(/\n|\r/g, '<br>');
  }

  toggleLanguage(): void {
    this.currentLang = !this.currentLang;
    this.updateApptLang();
  }

  capitalize(text?: string): string {
    return text ? _.capitalize(text) : '';
  }

  limitString(text: string, size: number): string {
    if (!text) return '';
    return text.length <= size ? text : text.substr(0, size) + '...';
  }

  getButtonStyle(btn: StatusButton): Record<string, string> {
    if (!this.appt) return {};
    const styles: Record<string, string> = {};

    if (btn.Status === 6 && this.appt.cancelBackColor) {
      styles['backgroundColor'] = `${this.appt.cancelBackColor} !important`;
      styles['borderColor'] = `${this.appt.cancelBackColor} !important`;
    }
    if (btn.Status === 6 && this.appt.cancelTextColor) {
      styles['color'] = `${this.appt.cancelTextColor} !important`;
    }
    if (btn.Status === 4 && this.appt.confirmBackColor) {
      styles['backgroundColor'] = `${this.appt.confirmBackColor} !important`;
      styles['borderColor'] = `${this.appt.confirmBackColor} !important`;
    }
    if (btn.Status === 4 && this.appt.confirmTextColor) {
      styles['color'] = `${this.appt.confirmTextColor} !important`;
    }
    if (btn.Status !== 4 && btn.Status !== 6) {
      if (btn.BackColor) {
        styles['backgroundColor'] = `${btn.BackColor} !important`;
        styles['borderColor'] = `${btn.BackColor} !important`;
      }
      if (btn.FontColor) {
        styles['color'] = btn.FontColor;
      }
    }

    return styles;
  }
}
